/**
 * Funnel API — extends the base funnel-api from lib/ with new endpoints.
 * Re-exports the original API for backward compatibility.
 */
import { changelogApi } from '../lib/funnel-api'
import apiClient from '../../../shared/lib/api'
import { getSelectedTenantId } from '../lib/tenant-selection'
import type {
  Funnel,
  FunnelStep,
  CreateFunnelPayload,
  UpdateFunnelPayload,
  CreateStepPayload,
  UpdateStepPayload,
  FunnelVersion,
  FunnelRule,
  RuleTrigger,
  TriggerType,
  TriggerOperator,
  OfferSet,
  OfferSetItem,
  CreateOfferSetPayload,
  AddOfferToSetPayload,
  Offer,
  CreateOfferPayload,
  UpdateOfferPayload,
  AnalyticsSummary,
  FunnelAnalytics,
  PricingViewEntry,
  Product,
  MatchMode,
} from '../types'

/** JSON-compatible value types returned from APIs */
type JsonPrimitive = string | number | boolean | null
type JsonValue = JsonPrimitive | JsonObject | JsonArray
interface JsonObject { [key: string]: JsonValue }
type JsonArray = JsonValue[]
type QueryValue = string | number | boolean | null | undefined
const OWNER_PUBLIC_BASE = '/v1/public'
const OWNER_API_ROOT = (import.meta.env.VITE_FUNNELS_OWNER_API_URL || '').replace(/\/+$/, '').replace(/\/v1$/, '')

/** Recursively convert object keys from snake_case to camelCase */
function snakeToCamel(obj: JsonValue): JsonValue {
  if (Array.isArray(obj)) return obj.map(snakeToCamel)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, val]) => [
        key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()),
        snakeToCamel(val),
      ])
    )
  }
  return obj
}

/** Shape of an Axios response wrapping a Go API envelope */
interface ApiResponse {
  data: JsonObject | JsonArray
}

/** Unwrap Go API { data: T } envelope and normalize keys.
 *  The generic T describes the expected shape after camelCase normalization. */
function unwrap<T = JsonObject>(response: ApiResponse): T {
  const body = response.data as JsonObject
  const inner = (body?.data ?? body) as JsonValue
  return snakeToCamel(inner) as T
}

function withTenantParams(params: Record<string, QueryValue> = {}): Record<string, QueryValue> {
  const tenantId = getSelectedTenantId()
  if (!tenantId) return params
  return { ...params, tenantId }
}

function ownerPath(path: string): string {
  if (OWNER_API_ROOT) {
    return `${OWNER_API_ROOT}${path}`
  }
  // apiClient.baseURL already includes /v1, strip it from path to avoid /v1/v1/
  return path.replace(/^\/v1/, '')
}

/** Go API funnel rule format (camelCase response from API) */
interface GoApiFunnelRule {
  id: string
  tenantId: string
  funnelId: string
  ruleType: 'product' | 'order_value' | 'product_count' | 'tag'
  comparator: 'any' | 'all' | 'notany' | 'notall' | 'lt' | 'le' | 'gt' | 'ge' | 'eq' | 'ne'
  value?: string
  productIds?: string[]
  isEnabled: boolean
  createdAt: string
  updatedAt: string
}

/** Go API rule creation payload */
interface CreateGoRulePayload {
  ruleType: string
  comparator: string
  value?: string
  productIds?: string[]
  isEnabled: boolean
}

interface UpdateMatchModePayload {
  matchMode: MatchMode
}

/** Map Go API rule to frontend FunnelRule format.
 * Each Go rule maps to ONE trigger in a FunnelRule. */
function mapGoRuleToFrontend(goRule: GoApiFunnelRule): FunnelRule {
  const trigger: RuleTrigger = {
    id: `trigger-${goRule.id}`,
    type: mapGoRuleTypeToTriggerType(goRule.ruleType),
    operator: mapGoComparatorToTriggerOperator(goRule.comparator),
    value: goRule.value ?? (goRule.productIds && goRule.productIds.length > 0 ? goRule.productIds : ''),
  }

  return {
    id: goRule.id,
    name: `Rule ${goRule.ruleType}`,
    logic: 'AND',
    triggers: [trigger],
    actions: [],
    isActive: goRule.isEnabled,
    priority: 0,
  }
}

/** Map frontend FunnelRule to Go API rule payloads.
 * Returns an array because multiple triggers create multiple Go rules. */
function mapFrontendRuleToGoRules(rule: FunnelRule): CreateGoRulePayload[] {
  return rule.triggers.map((trigger) => {
    const payload: CreateGoRulePayload = {
      ruleType: mapTriggerTypeToGoRuleType(trigger.type),
      comparator: mapTriggerOperatorToGoComparator(trigger.operator),
      isEnabled: rule.isActive,
    }

    if (Array.isArray(trigger.value)) {
      payload.productIds = trigger.value
    } else if (typeof trigger.value === 'string' && trigger.value !== '') {
      if (trigger.type === 'product_in_cart') {
        payload.productIds = [trigger.value]
      } else {
        payload.value = trigger.value
      }
    } else if (typeof trigger.value === 'number') {
      payload.value = String(trigger.value)
    }

    return payload
  })
}

/** Map Go API ruleType to frontend TriggerType */
function mapGoRuleTypeToTriggerType(ruleType: string): TriggerType {
  const mapping: Record<string, TriggerType> = {
    order_value: 'cart_value',
    product: 'product_in_cart',
    product_count: 'product_count',
    tag: 'customer_tag',
  }
  return mapping[ruleType] ?? 'cart_value'
}

/** Map frontend TriggerType to Go API ruleType */
function mapTriggerTypeToGoRuleType(triggerType: TriggerType): string {
  const mapping: Record<TriggerType, string> = {
    cart_value: 'order_value',
    product_in_cart: 'product',
    product_count: 'product_count',
    customer_tag: 'tag',
    geo_location: 'tag',
    device_type: 'tag',
    traffic_source: 'tag',
    time_on_page: 'order_value',
    page_url: 'tag',
  }
  return mapping[triggerType] ?? 'order_value'
}

/** Map Go API comparator to frontend TriggerOperator */
function mapGoComparatorToTriggerOperator(comparator: string): TriggerOperator {
  const mapping: Record<string, TriggerOperator> = {
    gt: 'greater_than',
    lt: 'less_than',
    eq: 'equals',
    ne: 'not_equals',
    ge: 'greater_than',
    le: 'less_than',
    any: 'in',
    all: 'in',
    notany: 'not_in',
    notall: 'not_in',
  }
  return mapping[comparator] ?? 'equals'
}

/** Map frontend TriggerOperator to Go API comparator */
function mapTriggerOperatorToGoComparator(operator: TriggerOperator): string {
  const mapping: Record<TriggerOperator, string> = {
    greater_than: 'gt',
    less_than: 'lt',
    equals: 'eq',
    not_equals: 'ne',
    contains: 'any',
    not_contains: 'notany',
    in: 'any',
    not_in: 'notany',
  }
  return mapping[operator] ?? 'eq'
}

/** Extended funnel API with new Lovable UI endpoints */
export const funnelApi = {
  // --- Re-export existing methods ---
  list: async (): Promise<{ data: Funnel[] }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels`), { params: withTenantParams() })
    const inner = unwrap<{ funnels?: Funnel[] }>(response)
    return { data: inner.funnels ?? [] }
  },
  get: async (id: string): Promise<{ data: Funnel }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${id}`), { params: withTenantParams() })
    const inner = unwrap<{ funnel: Funnel }>(response)
    return { data: inner.funnel }
  },
  create: (data: CreateFunnelPayload) => apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/funnels`), data, { params: withTenantParams() }),
  update: (id: string, data: UpdateFunnelPayload) => apiClient.put(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${id}`), data, { params: withTenantParams() }),
  delete: (id: string) => apiClient.delete(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${id}`), { params: withTenantParams() }),
  duplicate: (id: string) => apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${id}/duplicate`), null, { params: withTenantParams() }),

  // --- Steps (re-exported with typed payloads) ---
  listSteps: async (funnelId: string): Promise<{ data: FunnelStep[] }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/steps`), { params: withTenantParams() })
    const inner = unwrap<{ steps?: FunnelStep[] }>(response)
    return { data: inner.steps ?? [] }
  },
  getStep: async (funnelId: string, stepId: string): Promise<{ data: FunnelStep }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/steps/${stepId}`), { params: withTenantParams() })
    const inner = unwrap<{ step: FunnelStep }>(response)
    return { data: inner.step }
  },
  createStep: (funnelId: string, data: CreateStepPayload) =>
    apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/steps`), data, { params: withTenantParams() }),
  updateStep: (funnelId: string, stepId: string, data: UpdateStepPayload) =>
    apiClient.put(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/steps/${stepId}`), data, { params: withTenantParams() }),
  deleteStep: (funnelId: string, stepId: string) =>
    apiClient.delete(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/steps/${stepId}`), { params: withTenantParams() }),

  // --- Additional endpoints ---

  /** Get version history for a funnel (wraps changelog filtered by entity) */
  listVersions: async (funnelId: string): Promise<{ data: FunnelVersion[] }> => {
    try {
      const response = await changelogApi.list({ entityType: 'funnel' })
      const filtered = response.data
        .filter((entry) => entry.entityId === funnelId)
        .map((entry) => ({
          id: entry.id,
          version: 0,
          action: entry.action,
          actor: entry.actor,
          changes: entry.changes,
          createdAt: entry.createdAt,
        }))
      return { data: filtered }
    } catch {
      return { data: [] }
    }
  },

  listRules: async (funnelId: string): Promise<{ data: FunnelRule[] }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/rules`), { params: withTenantParams() })
    const inner = unwrap<{ rules?: GoApiFunnelRule[] }>(response)
    const goRules = inner.rules ?? []
    const frontendRules = goRules.map(mapGoRuleToFrontend)
    return { data: frontendRules }
  },

  /** Get rules for a funnel */
  getRules: async (funnelId: string): Promise<{ data: FunnelRule[] }> => {
    return funnelApi.listRules(funnelId)
  },

  getRule: async (funnelId: string, ruleId: string): Promise<{ data: FunnelRule }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/rules/${ruleId}`), { params: withTenantParams() })
    const inner = unwrap<{ rule: GoApiFunnelRule }>(response)
    return { data: mapGoRuleToFrontend(inner.rule) }
  },

  /** Create a new rule for a funnel */
  createRule: async (funnelId: string, rule: FunnelRule): Promise<{ data: FunnelRule }> => {
    const goRules = mapFrontendRuleToGoRules(rule)
    // For simplicity, create only the first trigger as a rule (multi-trigger support requires multiple API calls)
    if (goRules.length === 0) {
      throw new Error('Rule must have at least one trigger')
    }
    const response = await apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/rules`), goRules[0], { params: withTenantParams() })
    const inner = unwrap<{ rule: GoApiFunnelRule }>(response)
    return { data: mapGoRuleToFrontend(inner.rule) }
  },

  /** Update a rule for a funnel */
  updateRule: async (funnelId: string, ruleId: string, rule: FunnelRule): Promise<{ data: FunnelRule }> => {
    const goRules = mapFrontendRuleToGoRules(rule)
    if (goRules.length === 0) {
      throw new Error('Rule must have at least one trigger')
    }
    const response = await apiClient.put(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/rules/${ruleId}`), goRules[0], { params: withTenantParams() })
    const inner = unwrap<{ rule: GoApiFunnelRule }>(response)
    return { data: mapGoRuleToFrontend(inner.rule) }
  },

  /** Delete a rule from a funnel */
  deleteRule: async (funnelId: string, ruleId: string): Promise<void> => {
    await apiClient.delete(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/rules/${ruleId}`), { params: withTenantParams() })
  },

  updateMatchMode: async (funnelId: string, matchMode: MatchMode): Promise<void> => {
    const payload: UpdateMatchModePayload = { matchMode }
    await apiClient.patch(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/match-mode`), payload, { params: withTenantParams() })
  },

  /** Get page builder content for a step */
  getStepPage: async (funnelId: string, stepId: string): Promise<{ data: string }> => {
    try {
      const step = await funnelApi.getStep(funnelId, stepId)
      return { data: step.data.pageContent || '' }
    } catch {
      return { data: '' }
    }
  },

  /** Save page builder content for a step */
  saveStepPage: async (funnelId: string, stepId: string, content: string): Promise<void> => {
    await funnelApi.updateStep(funnelId, stepId, { pageContent: content })
  },

  /** Reorder a step by updating its position */
  reorderStep: async (funnelId: string, stepId: string, newPosition: number): Promise<void> => {
    await funnelApi.updateStep(funnelId, stepId, { position: newPosition })
  },

  // --- Offer Sets API ---

  /** List all offer sets */
  listOfferSets: async (): Promise<{ data: OfferSet[] }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets`), { params: withTenantParams() })
    const inner = unwrap<{ offerSets?: OfferSet[] }>(response)
    return { data: inner.offerSets ?? [] }
  },

  /** Get a single offer set by ID (includes items) */
  getOfferSet: async (id: string): Promise<{ data: OfferSet }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets/${id}`), { params: withTenantParams() })
    const inner = unwrap<{ offerSet: OfferSet }>(response)
    return { data: inner.offerSet }
  },

  /** Create a new offer set */
  createOfferSet: async (data: CreateOfferSetPayload): Promise<{ data: OfferSet }> => {
    const response = await apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets`), data, { params: withTenantParams() })
    const inner = unwrap<{ offerSet: OfferSet }>(response)
    return { data: inner.offerSet }
  },

  /** Update an offer set */
  updateOfferSet: async (id: string, data: Partial<CreateOfferSetPayload>): Promise<{ data: OfferSet }> => {
    const response = await apiClient.put(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets/${id}`), data, { params: withTenantParams() })
    const inner = unwrap<{ offerSet: OfferSet }>(response)
    return { data: inner.offerSet }
  },

  /** Delete an offer set */
  deleteOfferSet: async (id: string): Promise<void> => {
    await apiClient.delete(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets/${id}`), { params: withTenantParams() })
  },

  /** Add an offer to an offer set */
  addOfferToSet: async (setId: string, data: AddOfferToSetPayload): Promise<{ data: OfferSetItem }> => {
    const response = await apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets/${setId}/items`), data, { params: withTenantParams() })
    const inner = unwrap<{ item: OfferSetItem }>(response)
    return { data: inner.item }
  },

  /** Remove an offer from an offer set */
  removeOfferFromSet: async (setId: string, offerId: string): Promise<void> => {
    await apiClient.delete(ownerPath(`${OWNER_PUBLIC_BASE}/offer-sets/${setId}/items/${offerId}`), { params: withTenantParams() })
  },

  // --- Analytics API ---

  /** Get funnel analytics summary */
  getAnalytics: async (): Promise<{ data: AnalyticsSummary }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/analytics`), { params: withTenantParams() })
    const inner = unwrap<AnalyticsSummary>(response)
    return { data: inner }
  },

  /** Get pricing view — funnels with step/offer pricing breakdown */
  getPricingView: async (): Promise<{ data: PricingViewEntry[] }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/pricing-view`), { params: withTenantParams() })
    const inner = unwrap<{ entries: PricingViewEntry[] }>(response)
    return { data: inner.entries ?? [] }
  },

  /** Get per-funnel analytics */
  getFunnelAnalytics: async (funnelId: string): Promise<{ data: FunnelAnalytics }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/funnels/${funnelId}/analytics`), { params: withTenantParams() })
    const inner = unwrap<FunnelAnalytics>(response)
    return { data: inner }
  },

  // --- Products API ---

  /** List all products */
  listProducts: async (): Promise<{ data: Product[] }> => {
    const response = await apiClient.get('/products', { params: withTenantParams() })
    const inner = unwrap<{ products: Product[] }>(response)
    return { data: inner.products ?? [] }
  },

  // --- Offers API ---

  listOffers: async (funnelId?: string): Promise<{ data: Offer[] }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/offers`), {
      params: withTenantParams(funnelId ? { funnel_id: funnelId } : {}),
    })
    const inner = unwrap<{ offers?: Offer[] }>(response)
    return { data: inner.offers ?? [] }
  },

  getOffer: async (offerId: string): Promise<{ data: Offer }> => {
    const response = await apiClient.get(ownerPath(`${OWNER_PUBLIC_BASE}/offers/${offerId}`), { params: withTenantParams() })
    const inner = unwrap<{ offer: Offer }>(response)
    return { data: inner.offer }
  },

  createOffer: async (data: CreateOfferPayload): Promise<{ data: Offer }> => {
    const response = await apiClient.post(ownerPath(`${OWNER_PUBLIC_BASE}/offers`), data, { params: withTenantParams() })
    const inner = unwrap<{ offer: Offer }>(response)
    return { data: inner.offer }
  },

  updateOffer: async (offerId: string, data: UpdateOfferPayload): Promise<{ data: Offer }> => {
    const response = await apiClient.put(ownerPath(`${OWNER_PUBLIC_BASE}/offers/${offerId}`), data, { params: withTenantParams() })
    const inner = unwrap<{ offer: Offer }>(response)
    return { data: inner.offer }
  },

  deleteOffer: async (offerId: string): Promise<void> => {
    await apiClient.delete(ownerPath(`${OWNER_PUBLIC_BASE}/offers/${offerId}`), { params: withTenantParams() })
  },

  // Backward compatible wrappers
  listFunnelOffers: async (funnelId: string): Promise<{ data: Offer[] }> => {
    return funnelApi.listOffers(funnelId)
  },
  createFunnelOffer: async (funnelId: string, data: CreateOfferPayload): Promise<{ data: Offer }> => {
    return funnelApi.createOffer({ ...data, funnelId })
  },
}
